// ══════════════════════════════════════════════════════════════════════════════════════════════
//  t340 (21-oct-2026) · LucIA EN 3D COMO BOTÓN DE LA APLICACIÓN (función en desarrollo «asistente-lucia»)
// ══════════════════════════════════════════════════════════════════════════════════════════════
//
//  El dueño (21-oct): «cárgalo en la web directamente». Con la función «asistente-lucia» encendida para la clínica
//  (Superadmin → Funciones en desarrollo; de momento, solo la clínica 1; en pruebas, todas), el botón flotante de abajo
//  a la derecha deja de ser el globo verde del soporte y pasa a ser LucIA en 3D (components/lucia3d/Lucia3D.jsx).
//  Al pulsarla se abre el ASISTENTE LucIA nuevo (t341). El chat con el soporte de Biango de hoy no se pierde: se mueve
//  al «?» de arriba (utils/contactoBiango.jsx, «Chat con el soporte»), y el panel del chat se abre colgando de él.
//  Sin la función, todo sigue como hoy.
//
//  t353 · El dueño la vio a la izquierda y la quiere a la DERECHA: «creo que debería estar a la derecha, con un botón de
//  fondo chulo para que destaque más, y con una X roja que destaque arriba a la izquierda para cerrarlo momentáneamente y
//  que no moleste». Abajo a la derecha, sobre un círculo del color de la clínica (index.css, «EL FONDO DE LucIA»), con
//  su X roja: la esconde un rato (utils/luciaEscondida) y deja una pestañita en el borde derecho para sacarla.
//
//  POR QUÉ ESTÁ EN utils/: el globo (components/ChatWidget.jsx) lo montan decenas de guardias con sus dobles, y un
//  `utils/` se carga de verdad en todas sin tocar ninguna (test-helpers/utilDelFrontend). Por eso aquí solo se importa
//  react y otros utils; LucIA en 3D llega con import() (ni su componente ni three.js pesan en el arranque).
//  @pintarJsx-con-react: las guardias que pintan con el React de verdad lo cargan con él.
//  NO es utils/asistenteLucia.js (el del asistente, t341): este es solo el botón de la esquina y lo que lo rodea.
//  Guardias: backend/routes/lucia-boton-de-la-app.test.js y backend/routes/lucia-abajo-a-la-derecha.test.js.
// ══════════════════════════════════════════════════════════════════════════════════════════════
import { useEffect, useState } from 'react'
import { funcionEncendida, suscribirFunciones, fotoFunciones } from './funcionesEstado.js'
import { abrirAsistenteLucia } from './asistenteLucia.js'
import { luciaEscondida, esconderLucia, mostrarLucia, suscribirLuciaEscondida } from './luciaEscondida.js'
import { t, marca, tDato } from './i18n'   // B-2259 · los textos, en el idioma de quien mira (utils/i18n)

// ── EL ENGANCHE CON EL ASISTENTE (t341) ──────────────────────────────────────────────────────
// Pulsar a LucIA abre el ASISTENTE LucIA: su panel (components/asistenteLucia/PanelAsistenteLucia.jsx) lo monta App.jsx
// detrás de la misma función «asistente-lucia» que enciende este botón, y se abre con `abrirAsistenteLucia()`.
export const alPulsarLucia = () => abrirAsistenteLucia()

// Para quien no pueda importar el asistente: un aviso en la ventana que su panel ATIENDE (`atendido = true`, utils/asistenteLucia ·
// AVISO_DEL_BOTON_DE_LUCIA). Si nadie lo atiende, se abre el chat con el soporte: así nadie se queda sin ayuda.
export const AVISO_ABRIR_ASISTENTE = 'biango:abrir-asistente-lucia'
export const AVISO_ABRIR_SOPORTE = 'biango:abrir-soporte'
export function avisarAlAsistente(origen, w = typeof window !== 'undefined' ? window : null) {
  if (!w || typeof w.dispatchEvent !== 'function') return false
  const detalle = { origen: origen || 'boton', atendido: false }
  try { w.dispatchEvent(new CustomEvent(AVISO_ABRIR_ASISTENTE, { detail: detalle })) } catch (_) { /* sin eventos */ }
  if (detalle.atendido) return true
  abrirChatSoporte(w)
  return false
}
export function abrirChatSoporte(w = typeof window !== 'undefined' ? window : null) {
  try { w.dispatchEvent(new CustomEvent(AVISO_ABRIR_SOPORTE, { detail: { origen: 'lucia' } })) } catch (_) { /* sin eventos */ }
}

// ¿Va LucIA en el botón? Se vuelve a pintar cuando llega del servidor la lista de funciones encendidas.
export function useAsistenteLucia() {
  const [, setFoto] = useState(fotoFunciones())
  useEffect(() => suscribirFunciones(() => setFoto(fotoFunciones())), [])
  return funcionEncendida('asistente-lucia')
}

// t353 · ¿La ha escondido con su X? Se vuelve a pintar al esconderla, al sacarla y cuando pasa su rato.
export function useLuciaEscondida() {
  const [escondida, setEscondida] = useState(() => luciaEscondida())
  useEffect(() => suscribirLuciaEscondida(() => setEscondida(luciaEscondida())), [])
  return escondida
}

// Los valores de LucIA en el botón (los de la vista de pruebas: Superadmin → Funciones en desarrollo → LucIA en 3D;
// el JSON que se copia allí se pega aquí). Mide 76 px: el globo de hoy medía 52.
export const AJUSTES_BOTON_LUCIA = Object.freeze({ tamano: 76, flotacion: 1, velocidad: 1, balanceo: 1, giro: 1, halo: 0.5, fpsMax: 30 })
// La imagen quieta de LucIA (la misma que components/lucia3d/movimientoLucia.js · URL_POSTER; lo mira su guardia).
export const POSTER_LUCIA = '/lucia/lucia-quieta.webp?v=1'

// Lo que el resto de la página necesita saber, en atributos de <html> (ni el «?» ni los avisos necesitan hooks):
//  · con LucIA en la esquina, su alto (--lucia-esquina): lo que va abajo a la derecha se pone encima o a su izquierda y
//    lo que se desplaza deja abajo su hueco (index.css);
//  · escondida con su X (t353): el «?» enseña «Mostrar a LucIA»;
//  · los mensajes del soporte sin leer (el punto rojo del «?»), con LucIA en la esquina o escondida.
export function marcarEsquina(doc, { conLucia, alto, pendientes, escondida = false }) {
  const h = doc && doc.documentElement
  if (!h || !h.dataset) return
  if (conLucia) {
    h.dataset.esquinaLucia = ''
    if (h.style && typeof h.style.setProperty === 'function') h.style.setProperty('--lucia-esquina', alto + 'px')
  } else {
    delete h.dataset.esquinaLucia
    if (h.style && typeof h.style.removeProperty === 'function') h.style.removeProperty('--lucia-esquina')
  }
  if ((conLucia || escondida) && pendientes > 0) h.dataset.soportePendientes = String(pendientes > 99 ? '99+' : pendientes)
  else delete h.dataset.soportePendientes
  if (escondida) h.dataset.luciaEscondida = ''
  else delete h.dataset.luciaEscondida
}

const cargarLucia3D = () => import('../components/lucia3d/Lucia3D.jsx')

// ABAJO A LA DERECHA, con la zona segura de abajo y la de la derecha (el móvil tumbado).
export const SITIO_BOTON_LUCIA = Object.freeze({
  bottom: 'calc(16px + var(--sa-abajo))',
  right: 'calc(16px + var(--sa-der))',
})

// La X: 32 px para el dedo (lo mínimo que se pulsa bien en el móvil), con su círculo rojo de 20 dentro, sobresaliendo
// arriba a la izquierda del botón.
export const X_LUCIA = Object.freeze({ zona: 32, circulo: 20, fuera: 10 })
export const TEXTO_X_LUCIA = marca('utils.luciaEnLaEsquina.esconder_a_lucia_un_rato', 'Esconder a LucIA un rato')
export const TEXTO_MOSTRAR_LUCIA = marca('utils.luciaEnLaEsquina.mostrar_a_lucia', 'Mostrar a LucIA')

// EL BOTÓN DE LA ESQUINA. Mientras llega LucIA en 3D (o si no llega), su imagen quieta, igual que la que enseña Lucia3D
// mientras carga (sin salto al cambiar): el botón funciona desde el primer momento. Se esconde con una ventana abierta,
// como el globo de hoy (data-boton-flotante-ayuda). Detrás, su fondo y su halo (index.css); delante, la X, que es otro
// botón: su clic no llega a LucIA, así que no abre el asistente.
export function BotonLuciaFlotante({ onAbrir = alPulsarLucia, onEsconder = () => esconderLucia(), ajustes = AJUSTES_BOTON_LUCIA, cargar = cargarLucia3D }) {
  const [Lucia3D, setLucia3D] = useState(null)
  useEffect(() => {
    let vivo = true
    Promise.resolve().then(() => cargar())
      .then((m) => { if (vivo && m && m.default) setLucia3D(() => m.default) })
      .catch(() => { /* se queda la imagen quieta */ })
    return () => { vivo = false }
  }, [cargar])
  const lado = ajustes.tamano
  const quieta = (e) => { if (e && typeof e.stopPropagation === 'function') e.stopPropagation() }
  return (
    <div data-boton-flotante-ayuda="" data-boton-lucia="" style={{ position: 'fixed', ...SITIO_BOTON_LUCIA, zIndex: 2000, width: lado + 'px', height: lado + 'px' }}>
      <span data-halo-lucia="" aria-hidden="true" />
      <span data-fondo-lucia="" aria-hidden="true" />
      {Lucia3D
        ? <Lucia3D ajustes={ajustes} onClick={() => onAbrir('boton')} etiqueta={t('utils.luciaEnLaEsquina.abrir_el_asistente_lucia', 'Abrir el asistente LucIA')} />
        : (
          <button type="button" aria-label={t('utils.luciaEnLaEsquina.abrir_el_asistente_lucia', 'Abrir el asistente LucIA')} title={t('utils.luciaEnLaEsquina.abrir_el_asistente_lucia', 'Abrir el asistente LucIA')} onClick={() => onAbrir('boton')}
            style={{ position: 'relative', width: '100%', height: '100%', padding: 0, border: 'none', borderRadius: '50%', background: 'transparent', cursor: 'pointer' }}>
            <img src={POSTER_LUCIA} alt="" aria-hidden="true" draggable="false" style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }} />
          </button>
        )}
      <button type="button" data-cerrar-lucia="" aria-label={tDato(TEXTO_X_LUCIA)} title={t('utils.luciaEnLaEsquina.vuelve_a_las_dos_horas', '{{TEXTO_X_LUCIA}}: vuelve a las dos horas o al recargar la página', { TEXTO_X_LUCIA: tDato(TEXTO_X_LUCIA) })}
        onPointerDown={quieta} onMouseDown={quieta} onTouchStart={quieta}
        onClick={(e) => { quieta(e); if (e && typeof e.preventDefault === 'function') e.preventDefault(); onEsconder() }}
        style={{ position: 'absolute', top: -X_LUCIA.fuera + 'px', left: -X_LUCIA.fuera + 'px', width: X_LUCIA.zona + 'px', height: X_LUCIA.zona + 'px', padding: 0, margin: 0, border: 'none', background: 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', zIndex: 3, touchAction: 'manipulation' }}>
        <span data-cerrar-lucia-circulo="" aria-hidden="true" style={{ width: X_LUCIA.circulo + 'px', height: X_LUCIA.circulo + 'px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <svg width="10" height="10" viewBox="0 0 10 10" aria-hidden="true"><path d="M2 2 L8 8 M8 2 L2 8" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" /></svg>
        </span>
      </button>
    </div>
  )
}

// LA PESTAÑITA del borde derecho, mientras LucIA está escondida: pulsarla la vuelve a sacar. Pegada al borde, a la altura
// de LucIA y por debajo del aviso de errores de envío (que vuelve a su sitio de siempre, encima del globo).
export function PestanaLucia({ onMostrar = () => mostrarLucia() }) {
  return (
    <button type="button" data-pestana-lucia="" data-boton-flotante-ayuda="" aria-label={tDato(TEXTO_MOSTRAR_LUCIA)} title={tDato(TEXTO_MOSTRAR_LUCIA)}
      onClick={() => onMostrar()}
      style={{ position: 'fixed', right: 0, bottom: 'calc(20px + var(--sa-abajo))', width: '24px', height: '52px', padding: 0, border: 'none', borderRadius: '12px 0 0 12px', zIndex: 2000, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true"><path d="M7.5 2.5 L4 6 L7.5 9.5" stroke="currentColor" strokeWidth="1.8" fill="none" strokeLinecap="round" strokeLinejoin="round" /></svg>
    </button>
  )
}
